import { useState, useCallback, useMemo } from 'react';
import type { PhaseMapping, PhasePair } from '@/types/api';

interface UsePhaseSyncOptions {
  phaseMapping: PhaseMapping | null | undefined;
  templateFps?: number;
  athleteFps?: number;
  onPhaseChange?: (phaseIndex: number, phasePair: PhasePair | null) => void;
}

interface UsePhaseSyncResult {
  // State
  currentPhaseIndex: number;
  phaseProgress: number; // 0.0 - 1.0 within current phase
  phasePairs: PhasePair[];
  currentPhasePair: PhasePair | null;
  totalPhases: number;

  // Setters
  setPhaseProgress: (progress: number) => void;
  goToPhase: (index: number) => void;
  updatePhaseIndex: (index: number) => void; // Update index without resetting progress (for auto-transitions)
  nextPhase: () => void;
  prevPhase: () => void;

  // Time calculations
  getTemplateTime: (progress?: number, phaseIndex?: number) => number;
  getAthleteTime: (progress?: number, phaseIndex?: number) => number;
  getTemplateFrame: (progress?: number, phaseIndex?: number) => number;
  getAthleteFrame: (progress?: number, phaseIndex?: number) => number;

  // Timing info
  getCurrentTimingRatio: () => number;
  /** Timing ratio (athlete ÷ template duration) of a given phase index; 1 when unknown. */
  getTimingRatioForPhase: (phaseIndex: number) => number;
  getTimingIndicator: () => 'slower' | 'faster' | 'on-pace';

  // Phase progress from time
  calculateProgressFromTemplateTime: (time: number) => { phaseIndex: number; progress: number } | null;
  calculateProgressFromAthleteTime: (time: number) => { phaseIndex: number; progress: number } | null;

  // Status
  hasPhaseData: boolean;
  isEnabled: boolean;
}

/**
 * Hook for managing phase-aligned video synchronization.
 *
 * Phase-aligned sync works by synchronizing videos based on progress through each phase (0-100%)
 * rather than absolute frame numbers. This allows comparing movements with different durations.
 *
 * Example: If athlete takes longer to complete "Preparation" phase than template,
 * both videos show the same percentage through that phase at the same time.
 */
export function usePhaseSync(options: UsePhaseSyncOptions): UsePhaseSyncResult {
  const {
    phaseMapping,
    templateFps = 30,
    athleteFps = 30,
    onPhaseChange
  } = options;

  const [currentPhaseIndex, setCurrentPhaseIndex] = useState(0);
  const [phaseProgress, setPhaseProgress] = useState(0);

  // Get phase pairs from mapping
  const phasePairs = useMemo(() => {
    return phaseMapping?.phase_pairs || [];
  }, [phaseMapping]);

  const totalPhases = phasePairs.length;
  const hasPhaseData = totalPhases > 0;
  const isEnabled = hasPhaseData;

  // Current phase pair
  const currentPhasePair = useMemo(() => {
    if (currentPhaseIndex >= 0 && currentPhaseIndex < totalPhases) {
      return phasePairs[currentPhaseIndex];
    }
    return null;
  }, [phasePairs, currentPhaseIndex, totalPhases]);

  // Navigate to specific phase (resets progress to 0 - for user-initiated navigation)
  const goToPhase = useCallback((index: number) => {
    const clampedIndex = Math.max(0, Math.min(index, totalPhases - 1));
    if (clampedIndex !== currentPhaseIndex && totalPhases > 0) {
      setCurrentPhaseIndex(clampedIndex);
      setPhaseProgress(0); // Reset progress to start of phase
      onPhaseChange?.(clampedIndex, phasePairs[clampedIndex] || null);
    }
  }, [currentPhaseIndex, totalPhases, phasePairs, onPhaseChange]);

  // Update phase index without resetting progress (for auto-transitions during playback)
  const updatePhaseIndex = useCallback((index: number) => {
    const clampedIndex = Math.max(0, Math.min(index, totalPhases - 1));
    if (clampedIndex !== currentPhaseIndex && totalPhases > 0) {
      setCurrentPhaseIndex(clampedIndex);
      // Do NOT reset phaseProgress - this is for seamless transitions
      onPhaseChange?.(clampedIndex, phasePairs[clampedIndex] || null);
    }
  }, [currentPhaseIndex, totalPhases, phasePairs, onPhaseChange]);

  // Navigate to next phase
  const nextPhase = useCallback(() => {
    if (currentPhaseIndex < totalPhases - 1) {
      goToPhase(currentPhaseIndex + 1);
    }
  }, [currentPhaseIndex, totalPhases, goToPhase]);

  // Navigate to previous phase
  const prevPhase = useCallback(() => {
    if (currentPhaseIndex > 0) {
      goToPhase(currentPhaseIndex - 1);
    }
  }, [currentPhaseIndex, goToPhase]);

  // Set progress within current phase (0.0 - 1.0)
  const handleSetPhaseProgress = useCallback((progress: number) => {
    const clampedProgress = Math.max(0, Math.min(1, progress));
    setPhaseProgress(clampedProgress);
  }, []);

  // Calculate template time from phase progress
  const getTemplateTime = useCallback((progress?: number, phaseIndex?: number): number => {
    const idx = phaseIndex ?? currentPhaseIndex;
    const prog = progress ?? phaseProgress;

    if (idx < 0 || idx >= totalPhases) return 0;

    const pair = phasePairs[idx];
    if (!pair) return 0;

    const { template_phase } = pair;
    const startTime = template_phase.start_frame / templateFps;
    const duration = template_phase.duration_frames / templateFps;

    return startTime + (prog * duration);
  }, [currentPhaseIndex, phaseProgress, phasePairs, totalPhases, templateFps]);

  // Calculate athlete time from phase progress
  const getAthleteTime = useCallback((progress?: number, phaseIndex?: number): number => {
    const idx = phaseIndex ?? currentPhaseIndex;
    const prog = progress ?? phaseProgress;

    if (idx < 0 || idx >= totalPhases) return 0;

    const pair = phasePairs[idx];
    if (!pair) return 0;

    const { athlete_phase } = pair;
    const startTime = athlete_phase.start_frame / athleteFps;
    const duration = athlete_phase.duration_frames / athleteFps;

    return startTime + (prog * duration);
  }, [currentPhaseIndex, phaseProgress, phasePairs, totalPhases, athleteFps]);

  // Calculate template frame from phase progress
  const getTemplateFrame = useCallback((progress?: number, phaseIndex?: number): number => {
    const idx = phaseIndex ?? currentPhaseIndex;
    const prog = progress ?? phaseProgress;

    if (idx < 0 || idx >= totalPhases) return 0;

    const pair = phasePairs[idx];
    if (!pair) return 0;

    const { template_phase } = pair;
    return Math.round(template_phase.start_frame + (prog * template_phase.duration_frames));
  }, [currentPhaseIndex, phaseProgress, phasePairs, totalPhases]);

  // Calculate athlete frame from phase progress
  const getAthleteFrame = useCallback((progress?: number, phaseIndex?: number): number => {
    const idx = phaseIndex ?? currentPhaseIndex;
    const prog = progress ?? phaseProgress;

    if (idx < 0 || idx >= totalPhases) return 0;

    const pair = phasePairs[idx];
    if (!pair) return 0;

    const { athlete_phase } = pair;
    return Math.round(athlete_phase.start_frame + (prog * athlete_phase.duration_frames));
  }, [currentPhaseIndex, phaseProgress, phasePairs, totalPhases]);

  // Get current phase timing ratio
  const getCurrentTimingRatio = useCallback((): number => {
    if (!currentPhasePair) return 1.0;
    return currentPhasePair.timing_ratio;
  }, [currentPhasePair]);

  // Timing ratio of an arbitrary phase (pure, no state lag — the per-tick
  // athlete drive needs the ratio of the phase the TEMPLATE is in right now,
  // which can be one tick ahead of currentPhaseIndex).
  const getTimingRatioForPhase = useCallback((phaseIndex: number): number => {
    const pair = phasePairs[phaseIndex];
    const r = pair?.timing_ratio;
    return typeof r === 'number' && Number.isFinite(r) && r > 0 ? r : 1.0;
  }, [phasePairs]);

  // Get timing indicator based on ratio
  const getTimingIndicator = useCallback((): 'slower' | 'faster' | 'on-pace' => {
    const ratio = getCurrentTimingRatio();
    if (ratio > 1.1) return 'slower';  // Athlete took > 10% longer
    if (ratio < 0.9) return 'faster';   // Athlete took > 10% less time
    return 'on-pace';
  }, [getCurrentTimingRatio]);

  // Calculate progress from template time
  const calculateProgressFromTemplateTime = useCallback((time: number): { phaseIndex: number; progress: number } | null => {
    for (let i = 0; i < totalPhases; i++) {
      const pair = phasePairs[i];
      const { template_phase } = pair;
      const startTime = template_phase.start_frame / templateFps;
      const endTime = template_phase.end_frame / templateFps;

      if (time >= startTime && time <= endTime) {
        const duration = endTime - startTime;
        const progress = duration > 0 ? (time - startTime) / duration : 0;
        return { phaseIndex: i, progress: Math.max(0, Math.min(1, progress)) };
      }
    }
    return null;
  }, [phasePairs, totalPhases, templateFps]);

  // Calculate progress from athlete time
  const calculateProgressFromAthleteTime = useCallback((time: number): { phaseIndex: number; progress: number } | null => {
    for (let i = 0; i < totalPhases; i++) {
      const pair = phasePairs[i];
      const { athlete_phase } = pair;
      const startTime = athlete_phase.start_frame / athleteFps;
      const endTime = athlete_phase.end_frame / athleteFps;

      if (time >= startTime && time <= endTime) {
        const duration = endTime - startTime;
        const progress = duration > 0 ? (time - startTime) / duration : 0;
        return { phaseIndex: i, progress: Math.max(0, Math.min(1, progress)) };
      }
    }
    return null;
  }, [phasePairs, totalPhases, athleteFps]);

  return {
    // State
    currentPhaseIndex,
    phaseProgress,
    phasePairs,
    currentPhasePair,
    totalPhases,

    // Setters
    setPhaseProgress: handleSetPhaseProgress,
    goToPhase,
    updatePhaseIndex,
    nextPhase,
    prevPhase,

    // Time calculations
    getTemplateTime,
    getAthleteTime,
    getTemplateFrame,
    getAthleteFrame,

    // Timing info
    getCurrentTimingRatio,
    getTimingRatioForPhase,
    getTimingIndicator,

    // Phase progress from time
    calculateProgressFromTemplateTime,
    calculateProgressFromAthleteTime,

    // Status
    hasPhaseData,
    isEnabled,
  };
}
